import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, GripVertical, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import AdminLayout from '@/components/admin/AdminLayout';
import { faqsAPI } from '@/lib/api';
import { toast } from 'sonner';

const initialForm = { question: '', answer: '', display_order: 0, is_active: true };

export const AdminFAQ = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);

  const fetchFaqs = async () => {
    try {
      const res = await faqsAPI.getAll();
      setFaqs(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchFaqs(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...initialForm, display_order: faqs.length });
    setDialogOpen(true);
  };

  const openEdit = (f) => {
    setEditingId(f.id);
    setForm({ question: f.question, answer: f.answer, display_order: f.display_order || 0, is_active: f.is_active !== false });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.question.trim() || !form.answer.trim()) {
      toast.error('Pergunta e resposta sao obrigatorias');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await faqsAPI.update(editingId, form);
        toast.success('FAQ atualizada');
      } else {
        await faqsAPI.create(form);
        toast.success('FAQ criada');
      }
      setDialogOpen(false);
      fetchFaqs();
    } catch (e) { toast.error('Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await faqsAPI.delete(deleteId);
      toast.success('FAQ removida');
      setDeleteId(null);
      fetchFaqs();
    } catch (e) { toast.error('Erro ao remover'); }
  };

  return (
    <AdminLayout title="FAQ">
      <div className="space-y-4 max-w-4xl">
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">{faqs.length} perguntas cadastradas</p>
          <Button onClick={openCreate} className="bg-orange-500 hover:bg-orange-600" data-testid="add-faq-btn">
            <Plus className="w-4 h-4 mr-2" /> Nova Pergunta
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
        ) : faqs.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-slate-400">Nenhuma FAQ cadastrada</CardContent></Card>
        ) : (
          <div className="space-y-2">
            {faqs.map((f, index) => (
              <Card key={f.id} className={`${!f.is_active ? 'opacity-50' : ''}`} data-testid={`faq-${f.id}`}>
                <CardContent className="p-4 flex items-start gap-3">
                  <div className="text-slate-300 mt-1 flex-shrink-0">
                    <GripVertical className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded font-mono">#{index + 1}</span>
                      {!f.is_active && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Inativo</span>}
                    </div>
                    <h3 className="font-medium text-slate-900 text-sm mb-1">{f.question}</h3>
                    <p className="text-sm text-slate-500 line-clamp-2">{f.answer}</p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(f)} className="text-slate-400 hover:text-blue-500">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setDeleteId(f.id)} className="text-slate-400 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar FAQ' : 'Nova FAQ'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Pergunta</Label>
              <Input value={form.question} onChange={(e) => setForm(p => ({ ...p, question: e.target.value }))} placeholder="Como funciona o site?" />
            </div>
            <div>
              <Label>Resposta</Label>
              <Textarea value={form.answer} onChange={(e) => setForm(p => ({ ...p, answer: e.target.value }))} placeholder="Resposta detalhada..." rows={4} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Ordem de Exibicao</Label>
                <Input type="number" min={0} value={form.display_order} onChange={(e) => setForm(p => ({ ...p, display_order: parseInt(e.target.value) || 0 }))} />
              </div>
              <div className="flex items-center justify-between pt-6">
                <Label>Ativo</Label>
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm(p => ({ ...p, is_active: v }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingId ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Alert */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover FAQ?</AlertDialogTitle>
            <AlertDialogDescription>Esta acao nao pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default AdminFAQ;
